/* eslint-disable @typescript-eslint/no-misused-promises */
import { getChatList } from "../../pages/Agent/service/service";
import { useRequest } from "ahooks";
import { useErrorTips } from "../../hooks/useErrorTips";
import { useLocalState } from "../../pages/Agent/ChatPage/hooks/useLocalState";
import { useRequestController } from '../../pages/Agent/Home/hooks/useRequestController';
import { AGENT_MONTHLY_QUOTA_LIMIT_USD } from '@/pages/aigc/pages/Agent/config/constant';
import { useMemo } from 'react';
import { v4 as uuid } from 'uuid';

export interface ChatList {
  has_sensitive_data: boolean;
  sensitive_data_types?: { dashboard?: boolean; mgmt?: boolean; };
  session_id: string;
  timestamp: string;
  title: string;
  bookmarked?: boolean;
  agent_type?: string;
  metadata?: {
    model_id?: string;
    mode?: string;
    function?: {
      name?: string;
      source?: string;
    };
    db_args?: {
      enable?: boolean;
    };
  };
  // 后端附带的输出元数据：cos_list 有值表示该会话已生成 COS 文件（如 ai_gallery_file），
  // 用于在 HeaderMenu 中判定是否禁用"返回 Copilot"按钮
  output_metadata?: {
    cos_list?: string[];
  };
}

type TCHatList = {
  manual?: boolean;
};

interface ChatResponse {
  chats: ChatList[];
  bookmarked_chats: ChatList[];
}

/**
 * Token 用量配额 Hook
 * - 通过 ahooks cacheKey（tokenUsageQuota）在 TokenUsageQuota 组件与 refreshAfterChat 之间共享缓存
 * - TokenUsageQuota 挂载时自动请求；对话结束后由 refreshAfterChat 触发 refresh
 */
export const useTokenUsageQuota = ({ manual }: Partial<TCHatList> = {}) => {
  const { data, loading, run: refresh } = useRequestController({
    method: 'POST',
    url: '/api/agent_com/user_usage_cost',
    refreshDeps: useMemo(() => ({ trace_id: uuid() }), []),
    httpCfg: {
      rawData: true,
      ignoreCode: true,
    },
    manual: manual ?? false,
    cacheKey: 'tokenUsageQuota',
  });

  const usedAmount = data?.model_cost_usd || 0;
  const modelCostLimit = data?.model_cost_limit || AGENT_MONTHLY_QUOTA_LIMIT_USD;
  const percentage = Math.min((usedAmount / modelCostLimit) * 100, 100);
  return {
    data,
    loading,
    usedAmount,
    percentage,
    refresh,
  };
};

const useChatList = ({ manual }: TCHatList = {}) => {
  const showErrorTips = useErrorTips();
  const [data, setSessionChatList] = useLocalState<ChatResponse | null>(`__dba_chat_list__`, null);
  const { refresh: refreshTokenUsage } = useTokenUsageQuota({ manual: true});
  const { loading, run: fetchChatList } = useRequest<ChatResponse, any>(
    () => getChatList(showErrorTips) as unknown as Promise<ChatResponse>,
    {
      manual: manual ?? false,
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      onSuccess(res) {
        setSessionChatList(res);
      },
      onError() {
        setSessionChatList({ chats: [], bookmarked_chats: [] });
      },
    }
  );

  /** 对话结束后调用：刷新会话列表 + token 用量 */
  const refreshAfterChat = () => {
    fetchChatList();
    refreshTokenUsage();
  };

  const { chats: chatList = [], bookmarked_chats: bookMarkedChats = [] }: ChatResponse =
    data ?? { chats: [], bookmarked_chats: [] };

  return {
    chatList,
    bookMarkedChats,
    loading,
    fetchChatList,
    /** 对话结束后调用 */
    refreshAfterChat,
  };
};

export { useChatList };
